import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../constants";
import { ReportType } from "./report-types";
import { ItemByIdPath, ItemPath } from "./get-report";

/**
 * Regeneration re-runs the generator for a report's identity. There are two
 * modes:
 *
 *  - **Replay** (default, and the only mode for RoadSense): re-runs against the
 *    report's already-persisted `job.json`, so there is nothing for the caller
 *    to supply beyond the report's identity in the URL.
 *  - **Up-to-now** (WasteSense): the caller supplies `upToTimestamp` — an
 *    instant that identifies the week to regenerate (e.g. a just-reported dump's
 *    timestamp). The handler rebuilds a fresh job for that week (rather than
 *    replaying the stored one) and creates the report if the week has none yet.
 *    The instant only selects the week; the window is chosen server-side (up to
 *    *now* for the current open week, or the whole Mon→Sun week if it's already
 *    closed) so a late-reported dump can never shrink existing coverage.
 *    Optional so RoadSense's empty-body POST and the plain "recompute now"
 *    WasteSense replay both still validate.
 *
 * POST still requires a body schema (`defineEndpoint`'s POST overload); an
 * absent `upToTimestamp` (or `{}`) selects the replay mode.
 */
export const RegenerateReportBody = z.object({
  upToTimestamp: z.iso.datetime().optional(),
});
export type RegenerateReportBody = z.output<typeof RegenerateReportBody>;

/**
 * Fire-and-forget acknowledgement. Regeneration is asynchronous: the endpoint
 * enqueues the stored job onto the generator's SQS queue and returns
 * immediately (HTTP 200) with `status: "generating"`. The caller polls the
 * by-id fetch (`{type}/items/by-id/{reportId}`) to observe the flip back to
 * `ready`.
 *
 * (The response is a normal 200 rather than a bare 202 because the shared
 * `route()`/`defineEndpoint` layer serialises a typed body as 200 and validates
 * it against this schema; the `status: "generating"` field carries the
 * "accepted, working on it" meaning, and the client treats any 2xx identically.)
 *
 * `reportId` is echoed so a caller that regenerated by `(type, periodStart)`
 * learns the stable id to poll on. It never changes across a regeneration —
 * the generator's upsert is write-once on the report's identity.
 */
export const RegenerateReportResponse = z.object({
  data: z.object({
    reportId: z.string(),
    type: ReportType,
    periodStart: z.string(),
    status: z.literal("generating"),
  }),
});
export type RegenerateReportResponse = z.output<typeof RegenerateReportResponse>;

/**
 * Regenerate by the stable `reportId` (the canonical identity — the only key
 * unique for cumulative RoadSense reports). Resolves via GSI2, which is NOT
 * tenant-scoped, so the handler owns the tenant-match 403 (mirrors
 * `getReportByIdEndpoint`).
 */
export const regenerateReportByIdResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/id/{reportId}/regenerate",
  resourcePathSchema: ItemByIdPath,
  scope: "tenant",
});

export const regenerateReportByIdEndpoint = defineEndpoint(regenerateReportByIdResource, {
  method: "POST",
  bodySchema: RegenerateReportBody,
  responseSchema: RegenerateReportResponse,
});

/**
 * Regenerate by `(type, periodStart)` — the natural-key surface. Resolves via
 * the main index, which is tenant-partitioned, so a returned row provably
 * belongs to the caller (no cross-tenant guard needed; mirrors
 * `getReportEndpoint`). For a type with one report per period (WasteSense) this
 * is unambiguous; for RoadSense the cumulative report is the sole row sharing
 * that `periodStart`.
 */
export const regenerateReportByPeriodResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/period-start/{periodStart}/regenerate",
  resourcePathSchema: ItemPath,
  scope: "tenant",
});

export const regenerateReportByPeriodEndpoint = defineEndpoint(regenerateReportByPeriodResource, {
  method: "POST",
  bodySchema: RegenerateReportBody,
  responseSchema: RegenerateReportResponse,
});
